<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.container"
  >
    <SearchBoxPrimary
      v-model="input"
      placeholder="Search"
      @search="searchSurveyStates"
      :style="style.searchBox"
    />
    <CardPrimary :style="style.card">
      <TableColumnPrimary
        :rows="tenantUsers"
        :style="style.columnName"
      >
        <template v-slot:header>
          Name
        </template>
        <template v-slot:row="props">
          <UserImagePrimary
            :disabled="props.disabled"
            :tenantUser="props.row"
            :style="style.image"
          />
          <LinkNamePrimary
            :disabled="props.disabled"
            :tenantUser="props.row"
            :truncate="true"
          />
        </template>
      </TableColumnPrimary>
      <TableColumnPrimary
        :rows="titles"
        :style="style.columnTitle"
      >
        <template v-slot:header>
          Title
        </template>
        <template v-slot:row="props">
          <TextRegular :truncate="true">
            {{ props.row }}
          </TextRegular>
        </template>
      </TableColumnPrimary>
      <TableColumnPrimary
        :rows="ownedSkillsCounts"
        :style="style.columnOwnedSkillsCount"
      >
        <template v-slot:header>
          Reported skills
        </template>
        <template v-slot:row="props">
          {{ props.row }}
        </template>
      </TableColumnPrimary>
      <TableColumnPrimary
        :rows="completedDates"
        :style="style.columnCompletedDate"
      >
        <template v-slot:header>
          Completed
          <GoogleMaterialIconPrimary
            :content="'arrow_upward'"
            :style="style.iconCompleted"
          />
        </template>
        <template v-slot:row="props">
          {{ props.row }}
        </template>
      </TableColumnPrimary>
    </CardPrimary>
    <TextRegular>
      Total responses <span :style="style.responses">{{ surveyStatesCount }}</span>
    </TextRegular>
    <PaginationPrimary
      v-if="pageCount > 1"
      v-model="page"
      :pageCount="pageCount"
      @change="fetchSurveyStates(null)"
      :style="style.pagination"
    />
  </div>
</template>

<script>
  import ApiSurveyState from '@js/api/survey-state'
  import CardPrimary from '@modules/v2/card/card-primary'
  import GoogleMaterialIconPrimary
    from '@modules/v2/icon/google-material-icon-primary'
  import LinkNamePrimary from '@modules/v2/link/link-name-primary'
  import moment from 'moment'
  import PaginationPrimary from '@modules/v2/pagination/pagination-primary'
  import SearchBoxPrimary from '@modules/v2/input/search-box-primary'
  import TableColumnPrimary from '@modules/v2/table/table-column-primary'
  import TextRegular from '@modules/v2/text/text-regular'
  import UserImagePrimary from '@modules/v2/image/user-image-primary'
  import { FontStyle, Layout } from '@js/styles'
  import { mapGetters } from 'vuex'
  import { PAGE_SIZES } from '@js/constants'

  export default {
    name: 'AdminSurveyResponses',
    inheritAttrs: false,
    components: {
      CardPrimary,
      GoogleMaterialIconPrimary,
      LinkNamePrimary,
      PaginationPrimary,
      SearchBoxPrimary,
      TableColumnPrimary,
      TextRegular,
      UserImagePrimary,
    },
    props: {
      survey: {
        type: Object,
        required: false,
        default: () => {},
      },
      surveyCategorySkills: {
        type: Array,
        required: false,
        default: () => {},
      },
    },
    data: function () {
      return {
        input: '',
        isSearching: false,
        page: 1,
        pageSize: PAGE_SIZES.SURVEY_RESPONSE,
        surveyStates: [],
        surveyStatesCount: 0,
        style: {
          card: {
            display: 'flex',
            margin: `${Layout.space.md} 0`,
            overflowX: 'scroll',
            overflowY: 'none',
          },
          columnName: {
            flex: 2,
            minWidth: '180px',
          },
          columnOwnedSkillsCount: {
            flex: 1,
            minWidth: '128px',
          },
          columnTitle: {
            flex: 1,
            minWidth: '180px',
          },
          columnCompletedDate: {
            flex: 1,
            maxWidth: '128px',
            minWidth: '128px',
          },
          container: {},
          iconCompleted: {
            margin: `0 0 0 ${Layout.space.xs}`,
          },
          image: {
            margin: `0 ${Layout.space.sm} 0 0`,
          },
          pagination: {
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          },
          responses: {
            fontWeight: FontStyle.weight.xl,
          },
          searchBox: {
            maxWidth: '240px',
            width: '100%',
          },
        },
      };
    },
    created: async function () {
      await this.fetchSurveyStates();
    },
    computed: {
      ...mapGetters('globalVariables', ['getSurveyStateCompleted']),
      completedDates: function () {
        return this.surveyStates
          .map(elem => moment(elem.completedAt).format('MMM D YYYY'));
      },
      disableSearchBox: function () {
        return this.isSearching;
      },
      ownedSkillsCounts: function () {
        return this.tenantUsers.map(
          elem => elem.skills.filter(
            skillId => this.surveyCategorySkillIds.includes(skillId)
          ).length
        );
      },
      pageCount: function () {
        return Math.floor((this.surveyStatesCount - 1) / this.pageSize) + 1;
      },
      surveyCategorySkillIds: function () {
        return this.surveyCategorySkills.map(elem => elem?.skill?.id);
      },
      tenantUsers: function () {
        return this.surveyStates.map(elem => elem.tenantUser);
      },
      titles: function () {
        return this.surveyStates.map(elem => elem.tenantUser.title);
      },
    },
    methods: {
      fetchSurveyStates: async function (search = null) {
        try {
          const queryParams = {
            page: this.page,
            pageSize: this.pageSize,
            stateType: this.getSurveyStateCompleted,
            ...(
              search === undefined
              || search === null
                ? {}
                : {
                    search: search,
                  }
            ),
          };
          this.isSearching = await true;
          const api = await new ApiSurveyState(this.survey.id);
          const res = await api.get(queryParams);
          this.surveyStates = await res.data.results;
          this.surveyStatesCount = await res.data.count;
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to fetch responses',
          );
          this.$router.push({
            name: 'admin-surveys',
          });
        } finally {
          this.isSearching = false;
        }
      },
      searchSurveyStates: async function () {
        if (!this.disableSearchBox) {
          this.page = 1;
          this.surveyStatesCount = 0;
          await this.fetchSurveyStates(this.input);
        }
      },
    },
  };
</script>
